import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { PlayIcon, ShieldCheckIcon, CheckIcon, LockIcon } from 'lucide-react';
import { TrustedBy } from '../components/enterprise/TrustedBy';
import { StackCarousel } from '../components/enterprise/StackCarousel';
import { PlatformBento } from '../components/enterprise/PlatformBento';
import { GuidedStart } from '../components/enterprise/GuidedStart';
const CALENDLY = 'https://calendly.com/tonyennis/ai-adoption-for-your-business-clone';

/* Access control panel used in the hero. A model picker would only say "we
 have models"; for an enterprise buyer the screen that matters is the one
 answering "who can reach what", so the hero shows group-level access. */

const GROUPS = [{
  name: 'Everyone',
  members: 1240,
  tools: ['Slack', 'Google Drive', 'Confluence'],
  scope: 'Read-only knowledge'
}, {
  name: 'Revenue',
  members: 186,
  tools: ['Salesforce', 'Gong', 'HubSpot', 'Slack'],
  scope: 'Read and write'
}, {
  name: 'Engineering',
  members: 342,
  tools: ['GitHub', 'Linear', 'Sentry', 'Slack'],
  scope: 'Read and write'
}, {
  name: 'Finance',
  members: 41,
  tools: ['NetSuite', 'Ramp', 'Google Sheets'],
  scope: 'Restricted · approval required'
}];
function AccessPanel() {
  const [active, setActive] = useState(1);
  const group = GROUPS[active];
  return <div className="overflow-hidden rounded-2xl border border-neutral-200 bg-white shadow-[0_30px_70px_-25px_rgba(43,33,26,0.3)]" data-id="element-8516" data-mp="1nza1f9mjc4xz">
      <div className="flex items-center gap-2 border-b border-neutral-200 bg-[#FBFAF8] px-4 py-3" data-id="element-8517" data-mp="1sxbgzkkr5m4p">
        <span className="h-2.5 w-2.5 rounded-full bg-neutral-300" data-id="element-8518" data-mp="1xq4kk54hiof7" />
        <span className="h-2.5 w-2.5 rounded-full bg-neutral-300" data-id="element-8519" data-mp="22nwoxymbg30a" />
        <span className="h-2.5 w-2.5 rounded-full bg-neutral-300" data-id="element-8520" data-mp="22q9300oydt82" />
        <span className="ml-3 text-[12px] font-semibold text-neutral-400" data-id="element-8521" data-mp="27okd8cj5u5qt">
          Admin · Access control
        </span>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-[minmax(0,1fr)_minmax(0,1.15fr)]" data-id="element-8522" data-mp="2bmrwpf50dl5p">
        <div className="border-b border-neutral-200 sm:border-b-0 sm:border-r" data-id="element-8523" data-mp="ckcjl229nofn">
          <p className="px-5 pb-3 pt-5 text-[11px] font-bold uppercase tracking-[0.14em] text-neutral-400" data-id="element-8524" data-mp="7mazqex0nl34">
            Groups
          </p>
          <ul data-id="element-8525" data-mp="le9286w82iqb">
            {GROUPS.map((g, i) => <li key={g.name} data-id="element-8526" data-mp="glg3bznfq4m5" data-mpi={i}>
                <button type="button" onClick={() => setActive(i)} aria-pressed={i === active} className={`flex w-full items-center justify-between px-5 py-3 text-left transition-colors ${i === active ? 'bg-[#FFF7F0]' : 'hover:bg-neutral-50'}`} data-id="element-8527" data-mp="gkenlhvh4ty3">
                  <span className="flex items-center gap-2.5" data-id="element-8528" data-mp="14bsh3r5d2vkn">
                    <span className={`h-1.5 w-1.5 rounded-full ${i === active ? 'bg-[#EA580C]' : 'bg-neutral-300'}`} data-id="element-8529" data-mp="14968do6qia0u" />
                    <span className={`text-sm ${i === active ? 'font-bold text-[#2B211A]' : 'font-medium text-neutral-600'}`} data-id="element-8530" data-mp="362ngesys1cw4">
                      {g.name}
                    </span>
                  </span>
                  <span className="text-[12px] tabular-nums text-neutral-400" data-id="element-8531" data-mp="2rxddt9jlcn10">
                    {g.members.toLocaleString()}
                  </span>
                </button>
              </li>)}
          </ul>
        </div>

        <div className="p-5 pb-16" data-id="element-8532" data-mp="2i83h1gdsyg6k">
          <p className="text-[11px] font-bold uppercase tracking-[0.14em] text-neutral-400" data-id="element-8533" data-mp="2i71y6pqjopea">
            {group.name} can reach
          </p>
          <ul className="mt-3 space-y-2" data-id="element-8534" data-mp="288mgfddeqshg">
            {group.tools.map(t => <li key={t} className="flex items-center gap-2.5 rounded-lg border border-neutral-200 px-3 py-2 text-sm font-medium text-[#2B211A]" data-id="element-8535" data-mp="28b8nzd0at7zo">
                <CheckIcon size={14} strokeWidth={3} className="shrink-0 text-[#EA580C]" data-id="element-8536" data-mp="1z97sakkpqu04" />
                {t}
              </li>)}
          </ul>
          <div className="mt-4 flex items-center gap-2 border-t border-neutral-200 pt-4 text-[12.5px] text-neutral-500" data-id="element-8537" data-mp="247spsc8kyzkv">
            <LockIcon size={13} className="shrink-0" data-id="element-8538" data-mp="2g5wu554oulr" />
            {group.scope}
          </div>
        </div>
      </div>
    </div>;
}
export function EnterprisePlatform() {
  return <div className="min-h-screen bg-[#F4F3F1] font-body text-[#2B211A]" data-id="element-8539" data-mp="1yh8076anbrtp">
      {/* ── Hero ───────────────────────────────────────────────────────── */}
      <section className="px-6 pt-28 pb-4 md:px-10 md:pt-36" data-id="element-8540" data-mp="1okd1onedhrff">
        <div className="mx-auto max-w-[820px] text-center" data-id="element-8541" data-mp="1omz6wgdsjuuh">
          <motion.h1 initial={{
          opacity: 0,
          y: 16
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.5,
          ease: [0.23, 1, 0.32, 1]
        }} className="font-title text-4xl font-medium leading-[1.02] tracking-tight md:text-6xl lg:text-[72px]" data-id="element-8542" data-mp="1kizw8ef6zq91">
            The Platform for AI Adoption
          </motion.h1>

          <motion.p initial={{
          opacity: 0,
          y: 16
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.5,
          delay: 0.06,
          ease: [0.23, 1, 0.32, 1]
        }} className="mx-auto mt-6 max-w-[520px] text-lg leading-relaxed text-neutral-500 md:text-xl" data-id="element-8543" data-mp="10wv58ek5y564">
            Giga lets you roll AI out across your entire organisation, in a
            safe and flexible way.
          </motion.p>

          <motion.div initial={{
          opacity: 0,
          y: 16
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.5,
          delay: 0.12,
          ease: [0.23, 1, 0.32, 1]
        }} className="mt-9 flex flex-col items-center justify-center gap-3 sm:flex-row" data-id="element-8544" data-mp="3cidwg9llxx4a">
            <a href="https://app.getgiga.com/signup" className="inline-flex w-full items-center justify-center rounded-full bg-[#2B211A] px-7 py-3.5 font-bold text-white transition-colors hover:bg-[#3d2f25] sm:w-auto" data-id="element-8545" data-mp="2ou4fbkzc9xjh">
              Start free
            </a>
            <a href={CALENDLY} target="_blank" rel="noopener noreferrer" className="inline-flex w-full items-center justify-center rounded-full border border-[#2B211A]/15 bg-white px-7 py-3.5 font-bold text-[#2B211A] transition-colors hover:border-[#2B211A]/30 sm:w-auto" data-id="element-8546" data-mp="21407t2j15dn7">
              Talk to sales
            </a>
          </motion.div>
        </div>

        {/* Product panel with the tour pill sitting over it. */}
        <motion.div initial={{
        opacity: 0,
        y: 24
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.6,
        delay: 0.18,
        ease: [0.23, 1, 0.32, 1]
      }} className="relative mx-auto mt-16 max-w-[1000px]" data-id="element-8547" data-mp="3i6asvnwy8z0s">
          <AccessPanel data-id="element-8548" data-mp="2lg1ty4rudrbz" />
          <Link to="/demo" className="group absolute bottom-5 left-1/2 inline-flex -translate-x-1/2 items-center gap-3 rounded-full bg-[#2B211A]/90 py-2.5 pl-5 pr-2.5 text-white backdrop-blur-sm transition-colors hover:bg-[#2B211A]" data-id="element-8549" data-mp="2lgtqpix6dv3u">
            <span className="text-left" data-id="element-8550" data-mp="2uf5g5hmb2mup">
              <span className="block text-sm font-bold leading-tight" data-id="element-8551" data-mp="eosx4eugbigq">
                Watch product tour
              </span>
              <span className="block text-[12px] leading-tight text-white/60" data-id="element-8552" data-mp="9p7p7exazzrv">
                4 minutes
              </span>
            </span>
            <span className="flex h-8 w-8 items-center justify-center rounded-full bg-white/15 transition-colors group-hover:bg-white/25" data-id="element-8553" data-mp="i9maxfst90gr">
              <PlayIcon size={13} className="translate-x-[1px] fill-white text-white" data-id="element-8554" data-mp="89vhzijwetis" />
            </span>
          </Link>
        </motion.div>
      </section>

      <TrustedBy data-id="element-8555" data-mp="d9zxp6czfk7y" />

      <StackCarousel data-id="element-8556" data-mp="10xzpm9m15go4" />

      <PlatformBento data-id="element-8557" data-mp="10ysmmiv6srt2" />

      <GuidedStart data-id="element-8558" data-mp="10y9etuz93jsr" />

      {/* ── Closing ────────────────────────────────────────────────────── */}
      <section className="px-6 py-20 md:px-10 md:py-28" data-id="element-8559" data-mp="15wav685al50j">
        <div className="mx-auto max-w-[1100px] rounded-2xl bg-[#2B211A] px-8 py-16 text-center text-white md:px-16 md:py-20" data-id="element-8560" data-mp="r8g5z1qhtis7">
          <h2 className="mx-auto max-w-[16ch] font-title text-3xl font-medium leading-tight tracking-tight md:text-5xl" data-id="element-8561" data-mp="w5q1mz4h6qoh">
            Start with one team. Plan for all of them.
          </h2>
          <p className="mx-auto mt-5 max-w-[500px] text-lg leading-relaxed text-white/60" data-id="element-8562" data-mp="1oodghwsvp9h4">
            We will map your first rollout, the systems it touches, and the
            controls your security review will ask for.
          </p>
          <div className="mt-9 flex flex-col items-center justify-center gap-3 sm:flex-row" data-id="element-8563" data-mp="3fxnidsdpcgk5">
            <a href={CALENDLY} target="_blank" rel="noopener noreferrer" className="inline-flex w-full items-center justify-center rounded-full bg-[#EA580C] px-7 py-3.5 font-bold text-white transition-colors hover:bg-[#C2410C] sm:w-auto" data-id="element-8564" data-mp="3aykmaxfp9kob">
              Talk to sales
            </a>
            <Link to="/unlisted/security" className="inline-flex w-full items-center justify-center gap-2 rounded-full border border-white/20 px-7 py-3.5 font-bold text-white transition-colors hover:border-white/40 sm:w-auto" data-id="element-8565" data-mp="2ldamagvkals">
              <ShieldCheckIcon size={17} strokeWidth={2} data-id="element-8566" data-mp="3prw5u1nvljbg" />
              Security overview
            </Link>
          </div>
        </div>
      </section>
    </div>;
}